/**
 * SharedBetCard — a bet posted into a Community channel, in Floodlight
 * (v1.71): a rounded glass card that lifts with a purple bloom under the
 * pointer while an accent bar draws across the top. Tabular odds; positive EV
 * reads green, negative red. "Today's price" re-runs the original query so
 * readers see the current number, not the poster's.
 */
import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatAmerican } from '../../utils/communityShare';
import { FL, FONT, label, nums, FLButton, glass, ACCENT_GRADIENT } from '../floodlight';

export default function SharedBetCard({ bet, onRun, compact }) {
    if (!bet) return null;
    const ev = Number(bet.evPercent);
    const hasEv = Number.isFinite(ev);
    const sgp = bet.kind === 'sgp';
    return (
        <View
            style={[s.card, compact && { maxWidth: '100%', marginTop: 6 }]}
            dataSet={compact ? undefined : { flCard: '' }}
            accessible
            accessibilityLabel={`Shared bet: ${bet.title || bet.selection}${bet.odds != null ? `, ${formatAmerican(bet.odds)}` : ''}${bet.book ? ` at ${bet.book}` : ''}`}
        >
            <View style={s.bar} dataSet={{ flCardBar: '' }} />
            <View style={s.top}>
                <View style={[s.kind, sgp && s.kindSgp]}>
                    <Text style={[s.kindText, sgp && { color: FL.onGreen }]}>{sgp ? 'SGP' : 'BET'}</Text>
                </View>
                <Text style={s.title} numberOfLines={2}>{bet.title || bet.selection}</Text>
                {bet.odds != null && <Text style={[s.odds, nums]}>{formatAmerican(bet.odds)}</Text>}
            </View>
            {!!bet.legs?.length && (
                <View style={s.legs}>
                    {bet.legs.map((l, i) => (
                        <View key={`${l.label}${i}`} style={s.leg}>
                            <View style={s.legDot} />
                            <Text style={s.legText} numberOfLines={1}>{l.label}</Text>
                            {l.odds != null && <Text style={[s.legOdds, nums]}>{formatAmerican(l.odds)}</Text>}
                        </View>
                    ))}
                </View>
            )}
            <View style={s.meta}>
                {!!bet.book && <Text style={s.metaText}>{bet.book}</Text>}
                {hasEv && (
                    <View style={[s.ev, ev < 0 && s.evNeg]}>
                        <Ionicons name={ev >= 0 ? 'trending-up' : 'trending-down'} size={12} color={ev >= 0 ? FL.pos : FL.red} />
                        <Text style={[s.evText, nums, ev < 0 && { color: FL.red }]}>{ev > 0 ? '+' : ''}{ev.toFixed(1)}% EV</Text>
                    </View>
                )}
                {!!bet.query && onRun && (
                    <FLButton
                        label="Today's price"
                        variant="outline"
                        size="sm"
                        onPress={() => onRun(bet.query)}
                        accessibilityLabel={`Run the search ${bet.query}`}
                        style={s.run}
                    />
                )}
            </View>
        </View>
    );
}

const WEB = Platform.OS === 'web';

const s = StyleSheet.create({
    card: { ...glass({ radius: 16 }), gap: 10, marginTop: 10, padding: 14, paddingTop: 16, maxWidth: 520, overflow: 'hidden' },
    bar: { position: 'absolute', top: 0, left: 0, right: 0, height: 2, backgroundColor: FL.green, ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}) },
    top: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    kind: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: FL.stage, borderWidth: 1, borderColor: FL.hair2 },
    kindSgp: { backgroundColor: FL.green, borderColor: 'transparent', ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}) },
    kindText: { color: FL.onStage, fontFamily: FONT, fontSize: 10, fontWeight: '800', letterSpacing: 1.2 },
    title: { flex: 1, color: FL.ink, fontFamily: FONT, fontSize: 15, fontWeight: '700', letterSpacing: -0.2 },
    odds: { color: FL.ink, fontFamily: FONT, fontSize: 20, fontWeight: '800', letterSpacing: -0.4 },
    legs: { gap: 5, paddingTop: 10, borderTopWidth: 1, borderTopColor: FL.hair },
    leg: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    legDot: { width: 6, height: 6, borderRadius: 999, backgroundColor: FL.greenInk },
    legText: { flex: 1, color: FL.ink2, fontFamily: FONT, fontSize: 13 },
    legOdds: { color: FL.ink, fontFamily: FONT, fontSize: 13, fontWeight: '700' },
    meta: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    metaText: { ...label(11), color: FL.ink2 },
    ev: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: FL.posWash, borderWidth: 1, borderColor: 'rgba(74,222,128,0.4)' },
    evNeg: { backgroundColor: FL.redWash, borderColor: 'rgba(255,106,92,0.45)' },
    evText: { color: FL.pos, fontFamily: FONT, fontSize: 11, fontWeight: '800' },
    run: { marginLeft: 'auto', minHeight: 36 },
});
